import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, RefreshCw, Unlink, Users, AlertTriangle, AtSign, Film, Grid3X3 } from "lucide-react";
import { Facebook, Instagram, Linkedin, Twitter, Youtube } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ConnectAccountDialog } from "./ConnectAccountDialog";
import { formatDistanceToNow } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";

interface SocialAccount {
  id: string;
  platform: string;
  account_name: string;
  account_handle: string;
  avatar_url?: string;
  is_connected: boolean;
  follower_count: number;
  followers_count?: number;
  following_count?: number;
  posts_count?: number;
  last_synced_at?: string;
  token_expires_at?: string;
}

const TikTokIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.22 8.22 0 004.8 1.54V6.78a4.85 4.85 0 01-1.03-.09z"/>
  </svg>
);

const PinterestIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
  </svg>
);

const platformIcons: Record<string, React.ElementType> = {
  instagram: Instagram,
  twitter: Twitter,
  linkedin: Linkedin,
  facebook: Facebook,
  threads: AtSign,
  youtube: Youtube,
  tiktok: TikTokIcon,
  pinterest: PinterestIcon,
};

const platformColors: Record<string, string> = {
  instagram: "bg-gradient-to-r from-purple-500 to-pink-500",
  twitter: "bg-black",
  linkedin: "bg-blue-600",
  facebook: "bg-blue-500",
  threads: "bg-black",
  youtube: "bg-red-600",
  tiktok: "bg-black",
  pinterest: "bg-[#E60023]",
};

const platformLabel: Record<string, string> = {
  instagram: "Instagram",
  twitter: "X (Twitter)",
  linkedin: "LinkedIn",
  facebook: "Facebook",
  threads: "Threads",
  youtube: "YouTube",
  tiktok: "TikTok",
  pinterest: "Pinterest",
};

const followerLabel: Record<string, string> = {
  youtube: "subscribers",
  linkedin: "connections",
};

const postsLabel: Record<string, string> = {
  youtube: "videos",
  tiktok: "videos",
  pinterest: "pins",
};

export const SocialAccountsManager = ({ showHeader = true }: { showHeader?: boolean }) => {
  const { orgId } = useOrgPermissions();
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [showConnectDialog, setShowConnectDialog] = useState(false);
  const { toast } = useToast();

  useEffect(() => { fetchAccounts(); }, []);

  const fetchAccounts = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      let query = (supabase as any)
        .from('social_accounts')
        .select('id,platform,account_name,account_handle,avatar_url,is_connected,follower_count,followers_count,following_count,posts_count,last_synced_at,token_expires_at,access_token')
        .eq('user_id', user.id)
        .eq('is_connected', true);
      if (orgId) query = query.eq('org_id', orgId);
      const { data, error } = await query;
      if (error) throw error;
      setAccounts((data as SocialAccount[]) || []);
    } catch (error) {
      console.error("Error fetching accounts:", error);
      toast({ title: "Error", description: "Failed to load social accounts", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async (accountId: string) => {
    try {
      const { error } = await supabase.from('social_accounts').update({ is_connected: false }).eq('id', accountId);
      if (error) throw error;
      await fetchAccounts();
      toast({ title: "Account disconnected", description: "Social media account disconnected successfully." });
    } catch (error) {
      toast({ title: "Error", description: "Failed to disconnect account", variant: "destructive" });
    }
  };

  const handleSyncAll = async () => {
    if (accounts.length === 0) return;
    setSyncingAll(true);
    try {
      await Promise.allSettled(
        accounts.map(account =>
          supabase.functions.invoke(`sync-${account.platform}-account`, {
            body: { socialAccountId: account.id, accessToken: (account as any).access_token },
          })
        )
      );
      await fetchAccounts();
      toast({ title: "All accounts synced", description: "Latest metrics updated for all platforms." });
    } catch (_) {
      toast({ title: "Sync failed", description: "One or more accounts could not sync.", variant: "destructive" });
    } finally {
      setSyncingAll(false);
    }
  };

  const handleSync = async (accountId: string) => {
    setSyncing(accountId);
    const account = accounts.find(a => a.id === accountId);
    try {
      const { error } = await supabase.functions.invoke(`sync-${account?.platform ?? 'social'}-account`, {
        body: { socialAccountId: accountId, accessToken: (account as any)?.access_token },
      });
      if (error) throw error;
      await fetchAccounts();
      toast({ title: "Account synced", description: "Latest metrics updated successfully." });
    } catch (error: any) {
      toast({ title: "Sync failed", description: error.message || "Failed to sync account metrics", variant: "destructive" });
    } finally {
      setSyncing(null);
    }
  };

  if (loading) return <Card className="p-6">Loading accounts…</Card>;

  const connectedAccounts = accounts.filter(acc => acc.is_connected);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        {showHeader ? (
          <div>
            <h2 className="text-2xl font-bold">Connected Accounts</h2>
            <p className="text-muted-foreground">Manage your social media connections</p>
          </div>
        ) : <div />}
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleSyncAll} disabled={syncingAll || accounts.length === 0}>
            <RefreshCw className={`w-4 h-4 mr-2 ${syncingAll ? 'animate-spin' : ''}`} />
            {syncingAll ? 'Syncing…' : 'Sync All'}
          </Button>
          <Button className="gradient-primary" onClick={() => setShowConnectDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Connect Account
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {connectedAccounts.map((account) => {
          const Icon = platformIcons[account.platform];
          const colorClass = platformColors[account.platform] ?? 'bg-gray-500';
          const isExpired = account.token_expires_at ? new Date(account.token_expires_at) < new Date() : false;
          const expiresInDays = account.token_expires_at
            ? Math.ceil((new Date(account.token_expires_at).getTime() - Date.now()) / 86400000)
            : null;
          const isExpiringSoon = !isExpired && expiresInDays !== null && expiresInDays <= 7;
          const fCount = account.followers_count ?? account.follower_count ?? 0;
          const fLabel = followerLabel[account.platform] ?? 'followers';
          const pCount = account.posts_count;
          const pLabel = postsLabel[account.platform] ?? 'posts';

          return (
            <Card key={account.id} className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {/* Avatar with platform badge, or platform icon */}
                  <div className="relative shrink-0">
                    {account.avatar_url ? (
                      <img src={account.avatar_url} alt={account.account_name} className="w-12 h-12 rounded-xl object-cover" />
                    ) : (
                      <div className={`w-12 h-12 rounded-xl ${colorClass} flex items-center justify-center text-white`}>
                        {Icon && <Icon className="w-6 h-6" />}
                      </div>
                    )}
                    {account.avatar_url && Icon && (
                      <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full ${colorClass} flex items-center justify-center text-white ring-2 ring-background`}>
                        <Icon className="w-3 h-3" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{account.account_name || platformLabel[account.platform]}</p>
                    <p className="text-sm text-muted-foreground truncate">@{account.account_handle}</p>
                    <p className="text-xs text-muted-foreground/70">{platformLabel[account.platform] ?? account.platform}</p>

                    {/* Stats */}
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {fCount.toLocaleString()} {fLabel}
                      </span>
                      {(account.following_count ?? 0) > 0 && (
                        <span>{(account.following_count ?? 0).toLocaleString()} following</span>
                      )}
                      {pCount !== undefined && pCount !== null && pCount > 0 && (
                        <span className="flex items-center gap-1">
                          {account.platform === 'youtube' || account.platform === 'tiktok'
                            ? <Film className="w-3 h-3" />
                            : <Grid3X3 className="w-3 h-3" />}
                          {pCount.toLocaleString()} {pLabel}
                        </span>
                      )}
                      {account.last_synced_at && (
                        <span>· synced {formatDistanceToNow(new Date(account.last_synced_at), { addSuffix: true })}</span>
                      )}
                    </div>

                    {isExpired && (
                      <div className="flex items-center gap-1 mt-1 text-xs text-destructive">
                        <AlertTriangle className="w-3 h-3" />
                        Token expired — reconnect to restore posting
                      </div>
                    )}
                    {isExpiringSoon && (
                      <div className="flex items-center gap-1 mt-1 text-xs text-amber-600">
                        <AlertTriangle className="w-3 h-3" />
                        Token expires in {expiresInDays} day{expiresInDays !== 1 ? 's' : ''} — reconnect soon
                      </div>
                    )}
                  </div>
                </div>

                <Badge
                  variant={isExpired ? "destructive" : isExpiringSoon ? "outline" : "default"}
                  className={`shrink-0 ${isExpiringSoon ? "border-amber-400 text-amber-600" : ""}`}
                >
                  {isExpired ? "Expired" : isExpiringSoon ? `${expiresInDays}d left` : "Connected"}
                </Badge>
              </div>

              <div className="flex gap-2">
                {isExpired ? (
                  <Button size="sm" className="bg-[#006B3C] hover:bg-[#005a32] text-white" onClick={() => setShowConnectDialog(true)}>
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Reconnect
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => handleSync(account.id)} disabled={syncing === account.id}>
                    <RefreshCw className={`w-3 h-3 mr-1 ${syncing === account.id ? 'animate-spin' : ''}`} />
                    {syncing === account.id ? 'Syncing…' : 'Sync'}
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.confirm(`Disconnect ${account.account_name || account.platform}? Scheduled posts and DM sync for this account will stop immediately.`) && handleDisconnect(account.id)}
                >
                  <Unlink className="w-3 h-3 mr-1" />
                  Disconnect
                </Button>
              </div>
            </Card>
          );
        })}

        {connectedAccounts.length === 0 && (
          <Card className="p-8 col-span-2 text-center">
            <p className="text-muted-foreground mb-4">No social accounts connected yet</p>
            <Button className="gradient-primary" onClick={() => setShowConnectDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Connect Your First Account
            </Button>
          </Card>
        )}
      </div>

      <ConnectAccountDialog
        open={showConnectDialog}
        onOpenChange={setShowConnectDialog}
        onConnected={fetchAccounts}
      />
    </div>
  );
};
